import { Record } from 'immutable';
import { LocaleService, UrlService, ConfigsService } from '@sm360/widget-commons-core-services';
import { includes } from 'lodash';
import { REDIRECTIONS } from '@sm360/widget-commons-general';
import VehicleModelV25 from 'commons-vehicle/Vehicle/models/VehicleModelV25.js';
import { QUOTE, APPROVAL, DELIVERED } from 'commons-vehicle/Quote/constants/statusesV25.js';

const QuoteRecord = Record({
    id: '',
    leadId: 0,
    accountId: 0,
    status: 'quote',
    isDesking: true,
    isVisibleCustomer: true,
    isPreferred: true,
    isDeleted: false,
    isExpired: true,
    province: '',
    orgId: 0,
    orgUnitId: 0,
    purchaseMethod: '',
    vehicle: new VehicleModelV25(),
    cashPurchase: {
        sellingPrice: 0,
        tradeIn: 0,
        tradeInTaxCreditsDetails: {
            tradeIn: 0,
            tradeInTaxCredits: 0,
            taxesDetail: '',
            totalTradeIn: 0,
        },
        lien: 0,
        retailDetails: {
            msrp: 0,
            freightAndPDI: 0,
            taxesAndDuties: 0,
            total: 0,
        },
        incentives: {
            manufacturerRebates: 0,
            governmentRebates: 0,
            dealerRebates: 0,
            total: 0,
            deliveryBonus: 0,
            electricRebateIncluded: false,
        },
        feesDisclaimer: '',
        taxesDisclaimer: '',
        salesNumbers: '',
        taxesValidationAmount: {
            category: '',
            amount: 0,
        },
    },
    finance: {
        kmPerYearPlan: 0,
        paymentFrequency: 12,
        tradeIn: 0,
        tradeInTaxCreditsDetails: {
            tradeIn: 0,
            tradeInTaxCredits: 0,
            taxesDetail: '',
            totalTradeIn: 0,
        },
        lien: 0,
        cashDown: 0,
        cashDownTaxesDetails: {
            cashDown: 0,
            cashDownTaxes: 0,
            taxesDetail: '',
            totalCashDown: 0,
        },
        term: {
            term: 0,
            apr: 0,
            aprDetails: {
                initialApr: 0,
                adjustment: 0,
                totalApr: 0,
                totalInterestToPay: 0,
                totalObligation: 0,
                totalCreditCost: 0,
            },
            payment: 0,
            paymentTaxes: 0,
            taxesDetail: '',
            totalPayment: 0,
            residualDetails: {
                initialResidualRetail: 0,
                residualRetailAdjustment: 0,
                totalResidualRetail: 0,
                initialResidualRate: 0,
                residualRateAdjustment: 0,
                totalResidualRate: 0,
                calculatedResidualAmount: 0,
                residualAmountAdjustment: 0,
                totalResidualAmount: 0,
                buyBackFee: 0,
                buyBackAmount: 0,
            },
            amountFinanced: 0,
            dueAtDelivery: 0,
            securityDepositDetails: {
                securityDepositIsWaived: true,
                securityDepositAmount: 0,
                msdCount: 0,
                msdAmount: 0,
            },
        },
        sellingPrice: 0,
        incentives: {
            manufacturerRebates: 0,
            governmentRebates: 0,
            dealerRebates: 0,
            total: 0,
            deliveryBonus: 0,
            electricRebateIncluded: false,
        },
        feesDisclaimer: '',
        taxesDisclaimer: '',
        salesNumbers: '',
        taxesValidationAmount: {
            category: '',
            amount: 0,
        },
    },
    newIncentiveIsMoreFavorable: true,
    newIncentiveDisclaimer: '',
    creationDate: 0,
    modifiedDate: 0,
    quoteRevision: 0,
    transaction: [],
    modified: false,
    loading: false,
    error: '',
});

class QuoteV25 extends QuoteRecord {
    constructor(quote) {
        if (quote) {
            const vehicle = new VehicleModelV25(quote.vehicle);
            super({ ...quote, vehicle });
        } else {
            super();
        }
    }

    isQuoteInApproval() {
        return this.status === APPROVAL;
    }

    isQuoteDelivered() {
        return this.status === DELIVERED;
    }

    isQuoteInPending() {
        return this.status !== QUOTE && this.status !== APPROVAL;
    }

    isQuoteVehicleFromInventory() {
        return !!this.vehicle.vehicleId;
    }

    isCatalogDeskingQuote() {
        return !!this.isDesking && this.vehicle.vehicleId === 0;
    }

    getVehicleConditionType() {
        switch (this.vehicle.vehicleCondition) {
            case 'new':
                return LocaleService.t('VEHICLE_STATUS.NEW');
            case 'newDemonstrator':
                return LocaleService.t('VEHICLE_STATUS.DEMO');
            default:
                return LocaleService.t('VEHICLE_STATUS.USED');
        }
    }

    getVDPUrl(asShowroomV25 = false, isMarketPlace = false, orgUnitId = 0) {
        // this function is called by the garage and by AddToGarageV25 onClick
        const { vehicle } = this;
        const locale = LocaleService.getSimpleLocale();
        const isShowroomV25Enabled = ConfigsService.isShowroomV25Enabled() || asShowroomV25;
        const redirections = REDIRECTIONS[locale];
        let { vehicleId } = vehicle;

        let vdpURL;
        if (vehicle.getIsVehicleNew()) {
            vdpURL = vehicleId !== 0 ? redirections.vdpNew : redirections.buildAndQuote;
        } else {
            vdpURL = redirections.vdp;
        }

        const isCatalog = !vehicleId;

        if (isShowroomV25Enabled) {
            if (isCatalog) {
                vehicleId = vehicle.newCarTrimId || vehicleId;
                vdpURL = redirections.catalogMarketplace;
            } else if (isMarketPlace) {
                vdpURL = redirections.marketPlaceVdp;
            } else {
                vdpURL = vehicle.getIsVehicleNew() ? redirections.vdpNew : redirections.vdp;
            }
        }

        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{make}', vehicle.make.name);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{model}', vehicle.model.name);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{year}', vehicle.year);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{vehicleId}', vehicleId);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{bodyStyle}', vehicle.bodyStyle?.slug || '');
        vdpURL = vdpURL.toLowerCase().replace(/\s/g, '-');
        return this.buildUrl(vdpURL, !isShowroomV25Enabled && orgUnitId);
    }

    getVDPUrlFromListing() {
        const { vehicle } = this;
        const locale = LocaleService.getSimpleLocale();
        const redirections = REDIRECTIONS[locale];

        let vdpURL = redirections.vdp;
        const { vehicleId } = vehicle;

        if (vehicle.vehicleCondition === 'new') {
            vdpURL = redirections.vdpNew;
        }

        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{make}', vehicle.make.name);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{model}', vehicle.model.name);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{year}', vehicle.year);
        vdpURL = UrlService.replaceParamWithValue(vdpURL, '{vehicleId}', vehicleId);
        vdpURL = vdpURL.toLowerCase().replace(/\s/g, '-');

        return this.buildUrl(vdpURL);
    }

    getCheckoutUrl(orgUnitId) {
        const locale = LocaleService.getSimpleLocale();
        const url = REDIRECTIONS[locale].checkout;
        return this.buildUrl(url, orgUnitId);
    }

    isQuoteInGarage() {
        return !!(this.id && this.accountId !== 'public');
    }

    // TODO: Do we need this???? Check when do Quote in garage Ticket
    isDirty() {
        return !!(this.isQuoteInGarage() && this.modified);
    }

    buildUrl(url, orgUnitId) {
        const { id, securityToken } = this;
        const prefix = !includes(url, '?') ? '?' : '&';

        if (!url) {
            return '';
        }

        let fullUrl = `${url}${prefix}quoteId=${id}`;

        if (orgUnitId) {
            fullUrl += `&orgUnitId=${orgUnitId}`;
        }

        if (securityToken) {
            fullUrl += `&writeToken=${securityToken}`;
        }

        return fullUrl;
    }

    getShowroomQuote() {
        const {
            cashDown,
            financePlan,
            kmPerYearPlan,
            lien,
            paymentFrequency,
            purchaseMethod,
            saleType,
            sellingPrice,
            taxPlan,
            term,
            tradeIn,
        } = this.vehicle.purchaseMethods.getSelectedPriceSettings();

        const {
            make,
            model,
            odometer,
            showroomVehicleId,
            stockNumber,
            vehicleCondition,
            vehicleId,
            vin,
            year,
        } = this.vehicle.getShowroomVehicle();

        return {
            __revision: this.quoteRevision,
            id: String(this.id),
            location: ConfigsService.getOrganizationUnitLocation()?.toLowerCase() || '',
            dealerId: `${this.orgId}.${this.orgUnitId}`,
            status: this.status,
            creationDate: this.creationDate,
            modifiedDate: this.modifiedDate,
            expirationDate: this.expirationDate,
            accountId: this.accountId,
            leadId: this.leadId,
            retail: this.vehicle?.purchaseMethods?.cashPurchase?.retailDetails?.total || 0,

            // Purchase methods
            cashDown,
            financePlan,
            kmPerYearPlan,
            lien,
            paymentFrequency,
            purchaseMethod,
            saleType,
            sellingPrice,
            taxPlan,
            term,
            tradeIn,
            // transactionTokenCopy: transactionToken,

            // Vehicle
            make,
            model,
            odometer,
            showroomVehicleId,
            stockNumber,
            vehicleCondition,
            vehicleId,
            vin,
            year,
        };
    }
}

export default QuoteV25;
